import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { Plus, Pencil, Copy, Trash2, FileText, Receipt, Download, Upload, MoreVertical, CheckCircle2, ShoppingCart, Truck, ArrowLeftRight, LayoutTemplate } from "lucide-react";
import { DOC_TYPES, getDocType } from "./doc-types";
import { presetsFor } from "./presets";
import { buildSampleData } from "./sample-data";
import { previewHtml, compileTemplateHtml, ZP } from "./compile";
import { PAPER_PRESETS, newTemplate, normalizeTemplate } from "./schema";
import { listFormats, getFormat, deleteFormat, setDefaultFormat, saveFormat, listRenderedFormats } from "./api";
import { downloadFormats, readFormatFiles } from "./transfer";
import { Modal, PrimaryBtn, SecondaryBtn, NumInput, Check, Field } from "./editor/ui";
import successToast from "../../../helper-components/success-toast";
import errorToast from "../../../helper-components/error-toast";

// Scaled, non-interactive render of a template.
export function Thumbnail({ template, width = 170 }) {
  const sample = useMemo(() => buildSampleData(template.docType), [template.docType]);
  const html = useMemo(() => previewHtml(template, sample), [template, sample]);
  const ps = ZP.paperSize(template);
  const pagePx = (ps.width + 12) * ZP.MM_TO_PX;
  const scale = width / pagePx;
  const h = ps.continuous ? width * 1.5 : width * (ps.height / ps.width);
  return (
    <div className="overflow-hidden bg-gray-200 rounded border border-EAECF0" style={{ width, height: h }}>
      <iframe title="thumbnail" srcDoc={html} scrolling="no" style={{ width: pagePx, height: h / scale, transform: `scale(${scale})`, transformOrigin: "top left", border: 0, pointerEvents: "none" }} />
    </div>
  );
}

function NewFormatModal({ docType, onClose, onCreate }) {
  const presets = useMemo(() => presetsFor(docType), [docType]);
  const [choice, setChoice] = useState(presets[0] ? presets[0].key : "blank");
  const [paper, setPaper] = useState("A4");
  const [custom, setCustom] = useState({ width: 100, height: 150, continuous: false });
  const [name, setName] = useState("");
  const dt = getDocType(docType);

  const create = () => {
    let t;
    if (choice === "blank") {
      t = newTemplate(docType, paper);
      if (paper === "CUSTOM") t.paper = { ...t.paper, ...custom, height: custom.continuous ? 0 : custom.height };
    } else {
      t = presets.find((p) => p.key === choice).build();
    }
    t.name = name.trim() || (choice === "blank" ? `${dt.label} format` : t.name.replace(" - current format", " (custom)"));
    onCreate(normalizeTemplate(t));
  };

  return (
    <Modal
      title={`New ${dt.label} format`}
      onClose={onClose}
      width="max-w-4xl"
      footer={
        <>
          <SecondaryBtn onClick={onClose}>Cancel</SecondaryBtn>
          <PrimaryBtn onClick={create}>Open in builder</PrimaryBtn>
        </>
      }
    >
      <div className="p-4 flex flex-col gap-4">
        <Field label="Format name">
          <input className="border border-EAECF0 rounded px-2 py-1.5 text-sm" value={name} placeholder={`e.g. ${dt.label} - A5 with logo`} onChange={(e) => setName(e.target.value)} />
        </Field>
        <div>
          <div className="text-xs font-semibold text-000000 mb-2">Start from</div>
          <div className="flex flex-wrap gap-3">
            {presets.map((p) => (
              <button key={p.key} type="button" onClick={() => setChoice(p.key)} className={`flex flex-col gap-1 p-2 rounded border-2 text-left ${choice === p.key ? "border-primary" : "border-white hover:border-EAECF0"}`}>
                <Thumbnail template={p.build()} width={150} />
                <span className="text-[11px] text-000000 w-[150px] leading-tight">{p.name}</span>
              </button>
            ))}
            <button type="button" onClick={() => setChoice("blank")} className={`flex flex-col gap-1 p-2 rounded border-2 text-left ${choice === "blank" ? "border-primary" : "border-white hover:border-EAECF0"}`}>
              <div className="w-[150px] h-[212px] rounded border border-dashed border-667085 flex flex-col items-center justify-center text-667085 gap-2 bg-white">
                <Plus size={28} />
                <span className="text-xs">Blank</span>
              </div>
              <span className="text-[11px] text-000000">Start from scratch</span>
            </button>
          </div>
        </div>
        {choice === "blank" ? (
          <div>
            <div className="text-xs font-semibold text-000000 mb-2">Paper</div>
            <div className="grid grid-cols-7 gap-2">
              {PAPER_PRESETS.map((p) => (
                <button key={p.id} type="button" onClick={() => setPaper(p.id)} className={`border rounded px-2 py-2 text-[11px] leading-tight ${paper === p.id ? "border-primary bg-primary text-white" : "border-EAECF0 text-344054 hover:border-primary"}`}>
                  <div className="font-semibold flex items-center gap-1 justify-center">
                    {p.thermal ? <Receipt size={12} /> : <FileText size={12} />}
                    {p.label}
                  </div>
                  <div className={paper === p.id ? "text-white" : "text-667085"}>{p.desc}</div>
                </button>
              ))}
            </div>
            {paper === "CUSTOM" && (
              <div className="flex items-end gap-3 mt-3">
                <Field label="Width (mm)">
                  <NumInput value={custom.width} min={20} max={1000} onChange={(v) => setCustom({ ...custom, width: v })} />
                </Field>
                <Field label="Height (mm)">
                  <NumInput value={custom.height} min={20} max={2000} disabled={custom.continuous} onChange={(v) => setCustom({ ...custom, height: v })} />
                </Field>
                <Check checked={custom.continuous} onChange={(v) => setCustom({ ...custom, continuous: v })} label="Continuous roll (height follows content)" />
              </div>
            )}
          </div>
        ) : (
          <p className="text-[11px] text-667085">You can change the paper size (A4, A5, thermal rolls or a custom size) inside the builder at any time.</p>
        )}
      </div>
    </Modal>
  );
}

const GROUP_ICONS = { Billing: Receipt, Sales: ShoppingCart, Purchase: Truck, Inventory: ArrowLeftRight };

const THUMB_W = 236;
const THUMB_H = 190;

// Backend-rendered print page shown as a small, non-interactive preview.
function HtmlThumb({ html, paper }) {
  // roll paper is narrow: zoom in so the receipt fills the card
  const thermal = PAPER_PRESETS.find((p) => p.id === paper)?.thermal;
  const pageW = thermal ? 420 : 860;
  const scale = THUMB_W / pageW;
  return (
    <iframe
      title="preview"
      srcDoc={html}
      scrolling="no"
      loading="lazy"
      tabIndex={-1}
      style={{ width: pageW, height: THUMB_H / scale, transform: `scale(${scale})`, transformOrigin: "top left", border: 0, pointerEvents: "none", background: "#fff" }}
    />
  );
}

function ThumbFrame({ children, onClick, badge }) {
  return (
    <div className="relative bg-nav-head-bg overflow-hidden group cursor-pointer" style={{ height: THUMB_H }} onClick={onClick}>
      {children}
            {badge}
    </div>
  );
}

function InUseBadge() {
  return (
    <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-027A48 text-white text-[10px] font-semibold px-2 py-0.5 shadow">
      <CheckCircle2 size={11} /> In use
    </span>
  );
}

function PaperChip({ row }) {
  if (!row.is_builder) return <span className="inline-flex items-center gap-1 rounded bg-nav-head-bg text-344054 text-[10px] px-1.5 py-0.5">Old editor</span>;
  const p = PAPER_PRESETS.find((x) => x.id === row.paper);
  const Icon = p?.thermal ? Receipt : FileText;
  return (
    <span className="inline-flex items-center gap-1 rounded bg-nav-head-bg text-344054 text-[10px] px-1.5 py-0.5">
      <Icon size={11} /> {p ? p.label : row.paper || "A4"}
    </span>
  );
}

// Small ⋮ menu for secondary card actions.
function CardMenu({ items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button type="button" className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-nav-head-bg text-344054" onClick={() => setOpen(!open)} title="More actions">
        <MoreVertical size={15} />
      </button>
      {open && (
        <div className="absolute right-0 bottom-8 z-20 w-40 bg-white border border-EAECF0 rounded-md shadow-lg py-1">
          {items.filter(Boolean).map((it) => (
            <button
              key={it.label}
              type="button"
              disabled={it.disabled}
              onClick={() => {
                setOpen(false);
                it.onClick();
              }}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left disabled:opacity-40 ${it.danger ? "text-F51D00 hover:bg-F4F4F4" : "text-344054 hover:bg-f3f8ff"}`}
            >
              {it.icon} {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function timeAgo(d) {
  if (!d) return "";
  const t = new Date(d);
  const diff = (Date.now() - t.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} d ago`;
  return t.toLocaleDateString();
}

function SkeletonCard() {
  return (
    <div className="rounded-xl border border-EAECF0 bg-white overflow-hidden animate-pulse">
      <div className="bg-nav-head-bg" style={{ height: THUMB_H }} />
      <div className="p-3 flex flex-col gap-2">
        <div className="h-3 w-3/4 bg-nav-head-bg rounded" />
        <div className="h-3 w-1/3 bg-nav-head-bg rounded" />
      </div>
    </div>
  );
}

export default function FormatList({ initialType, onOpen }) {
  const navigate = useNavigate();
  const [type, setType] = useState(initialType || DOC_TYPES[0].key);
  const [allRows, setAllRows] = useState([]);
  const [rendered, setRendered] = useState({}); // code -> html
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(null);
  const [transferring, setTransferring] = useState(false);
  const importInput = useRef(null);
  const dt = getDocType(type);
  const rows = useMemo(() => allRows.filter((r) => r.type === type), [allRows, type]);

  const loadAll = useCallback(async () => {
    try {
      setAllRows((await listFormats()) || []);
    } catch (e) {
      setAllRows([]);
      errorToast(e?.message || "Could not load formats");
    }
  }, []);

  const loadPreviews = useCallback(async () => {
    try {
      const list = await listRenderedFormats(type);
      const byCode = {};
      list.forEach((f) => (byCode[String(f.code)] = f.html));
      setRendered(byCode);
    } catch {
      setRendered({});
    }
  }, [type]);

  const load = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadAll(), loadPreviews()]);
    setLoading(false);
  }, [loadAll, loadPreviews]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c = {};
    allRows.forEach((r) => (c[r.type] = (c[r.type] || 0) + 1));
    return c;
  }, [allRows]);
  const defaultRow = rows.find((r) => r.is_default);

  const makeDefault = async (format) => {
    setBusy(format);
    try {
      await setDefaultFormat(type, format);
      successToast(`${dt.label} will now print with this format`);
      loadAll();
    } catch (e) {
      errorToast(e?.message);
    } finally {
      setBusy(null);
    }
  };

  const loadTemplate = async (row) => {
    const full = await getFormat(row.id);
    if (!full?.template_json) return null;
    const t = normalizeTemplate(full.template_json);
    t.docType = full.type || type;
    t.name = full.name || t.name;
    return t;
  };

  const openSaved = async (row, duplicate) => {
    setBusy(row.id);
    try {
      const t = await loadTemplate(row);
      if (!t) {
        errorToast("This format was made with the old editor and cannot be opened here. Create a new one from a preset instead.");
        return;
      }
      if (duplicate) t.name = `${t.name} (copy)`;
      onOpen({ template: t, id: duplicate ? null : row.id, isDefault: !duplicate && row.is_default });
    } catch (e) {
      errorToast(e?.message);
    } finally {
      setBusy(null);
    }
  };

  const exportOne = async (row) => {
    setBusy(row.id);
    try {
      const t = await loadTemplate(row);
      if (!t) return errorToast("Formats made with the old editor cannot be exported");
      downloadFormats([t], t.name);
    } catch (e) {
      errorToast(e?.message);
    } finally {
      setBusy(null);
    }
  };

  const exportAll = async () => {
    const builderRows = rows.filter((r) => r.is_builder);
    if (!builderRows.length) return errorToast(`There are no ${dt.label} formats to export`);
    setTransferring(true);
    try {
      const templates = (await Promise.all(builderRows.map(loadTemplate))).filter(Boolean);
      downloadFormats(templates, `${dt.label} formats`);
    } catch (e) {
      errorToast(e?.message);
    } finally {
      setTransferring(false);
    }
  };

  // Each imported format is saved as a new format; nothing existing is replaced.
  const importFiles = async (files) => {
    if (!files || !files.length) return;
    setTransferring(true);
    try {
      const templates = await readFormatFiles(files, type);
      const taken = new Set(allRows.map((r) => `${r.type}|${r.name}`));
      for (const t of templates) {
        if (taken.has(`${t.docType}|${t.name}`)) t.name = `${t.name} (imported)`;
        await saveFormat({ type: t.docType, name: t.name, html: compileTemplateHtml(t), template_json: t, paper: t.paper?.preset || "A4" });
      }
      const types = [...new Set(templates.map((t) => t.docType))];
      successToast(`Imported ${templates.length} format${templates.length === 1 ? "" : "s"}`);
      if (types.length === 1 && types[0] !== type) setType(types[0]);
      else load();
    } catch (e) {
      errorToast(e?.message || "Import failed");
    } finally {
      setTransferring(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name || "format"}"?${row.is_default ? "\n\nIt is used for printing; prints will go back to the standard format." : ""}`)) return;
    setBusy(row.id);
    try {
      await deleteFormat(row.id);
      successToast("Format deleted");
      load();
    } catch (e) {
      errorToast(e?.message);
    } finally {
      setBusy(null);
    }
  };

  const groups = DOC_TYPES.reduce((m, d) => {
    (m[d.group] = m[d.group] || []).push(d);
    return m;
  }, {});

  const presetCurrent = presetsFor(type)[0];
  const customizeStandard = () => {
    const t = presetCurrent.build();
    t.name = t.name.replace(" - current format", " (custom)");
    onOpen({ template: t, id: null });
  };

  return (
    <div className="h-full flex flex-col" style={{ background: "#f8fafc" }}>
      {/* header */}
      <div className="px-6 pt-3 pb-4 bg-white border-b border-EAECF0">
        <button className="flex items-center gap-1 text-[11px] text-667085 hover:text-primary" onClick={() => navigate("/settings")}>
          <ChevronLeftIcon className="h-3.5 w-3.5" />
          System Settings
        </button>
        <div className="flex items-end justify-between gap-4 mt-1">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary text-white flex items-center justify-center">
              <LayoutTemplate size={20} />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-000000 leading-tight">Invoice Builder</h1>
              <p className="text-xs text-667085">Design how your invoices, sales, purchase and stock transfer documents print — on A4, A5, thermal rolls or any size.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <SecondaryBtn onClick={() => importInput.current && importInput.current.click()} disabled={transferring}>
              <Upload size={14} /> Import
            </SecondaryBtn>
            <input
              ref={importInput}
              type="file"
              accept="application/json,.json"
              multiple
              className="hidden"
              onChange={(e) => {
                importFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <SecondaryBtn onClick={exportAll} disabled={transferring || !rows.some((r) => r.is_builder)}>
              <Download size={14} /> Export all
            </SecondaryBtn>
            <PrimaryBtn onClick={() => setShowNew(true)}>
              <Plus size={14} /> New format
            </PrimaryBtn>
          </div>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* document types */}
        <nav className="w-64 shrink-0 bg-white border-r border-EAECF0 overflow-auto p-3">
          {Object.keys(groups).map((g) => {
            const Icon = GROUP_ICONS[g] || FileText;
            return (
              <div key={g} className="mb-4">
                <div className="flex items-center gap-2 px-2 mb-1 text-[10px] uppercase tracking-wider text-667085 font-semibold">
                  <Icon size={13} /> {g}
                </div>
                {groups[g].map((d) => {
                  const active = type === d.key;
                  return (
                    <button
                      key={d.key}
                      type="button"
                      onClick={() => setType(d.key)}
                      className={`w-full flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-xs transition-colors ${active ? "bg-primary text-white font-medium" : "text-344054 hover:bg-f3f8ff"}`}
                    >
                      <span className="truncate text-left">{d.label}</span>
                      {counts[d.key] > 0 && (
                        <span className={`min-w-[18px] h-[18px] px-1 rounded-full text-[10px] flex items-center justify-center ${active ? "bg-white text-primary" : "bg-nav-head-bg text-344054"}`}>{counts[d.key]}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* formats */}
        <main className="flex-1 overflow-auto p-6">
          <div className="flex items-center justify-between gap-4 mb-5">
            <div>
              <h2 className="text-base font-semibold text-000000">{dt.label}</h2>
              <p className="text-xs text-667085 mt-0.5">
                Printing with <span className="font-medium text-000000">{defaultRow ? defaultRow.name || "Untitled format" : "Standard format"}</span>. Copies and preview options are
                in Print Preferences.
              </p>
            </div>
          </div>

          <div className="grid gap-5" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${THUMB_W}px, ${THUMB_W}px))` }}>
            {/* built-in print */}
            <div className={`rounded-xl bg-white overflow-hidden flex flex-col transition-shadow hover:shadow-md ${!defaultRow ? "ring-2 ring-primary" : "border border-EAECF0"}`}>
              <ThumbFrame onClick={() => presetCurrent && customizeStandard()} badge={!defaultRow && !loading && <InUseBadge />}>
                {rendered["1"] ? <HtmlThumb html={rendered["1"]} /> : presetCurrent ? <Thumbnail template={presetCurrent.build()} width={THUMB_W} /> : null}
              </ThumbFrame>
              <div className="p-3 flex flex-col gap-2 border-t border-EAECF0 flex-1">
                <div>
                  <div className="text-sm font-semibold text-000000">Standard format</div>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="inline-flex items-center gap-1 rounded bg-nav-head-bg text-344054 text-[10px] px-1.5 py-0.5">
                      <FileText size={11} /> A4
                    </span>
                    <span className="text-[10px] text-667085">Built into the system</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-auto pt-1">
                  {presetCurrent && (
                    <button type="button" className="text-xs font-medium text-primary inline-flex items-center gap-1 hover:underline" onClick={customizeStandard}>
                      <Pencil size={13} /> Customize
                    </button>
                  )}
                  {defaultRow && (
                    <button type="button" className="ml-auto text-xs text-344054 inline-flex items-center gap-1 border border-EAECF0 rounded-md px-2 py-1 hover:bg-f3f8ff" disabled={busy === 1} onClick={() => makeDefault(1)}>
                      <CheckCircle2 size={13} /> Use
                    </button>
                  )}
                </div>
              </div>
            </div>

            {loading && !rows.length && [0, 1].map((i) => <SkeletonCard key={i} />)}

            {rows.map((r) => (
              <div key={r.id} className={`rounded-xl bg-white overflow-hidden flex flex-col transition-shadow hover:shadow-md ${r.is_default ? "ring-2 ring-primary" : "border border-EAECF0"} ${busy === r.id ? "opacity-60" : ""}`}>
                <ThumbFrame onClick={() => r.is_builder && openSaved(r, false)} badge={r.is_default && <InUseBadge />}>
                  {rendered[r.id] ? (
                    <HtmlThumb html={rendered[r.id]} paper={r.paper} />
                  ) : (
                    <div className="h-full flex items-center justify-center text-D0D5DD">
                      <FileText size={40} />
                    </div>
                  )}
                </ThumbFrame>
                <div className="p-3 flex flex-col gap-2 border-t border-EAECF0 flex-1">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-000000 truncate" title={r.name}>
                      {r.name || "Untitled format"}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <PaperChip row={r} />
                      <span className="text-[10px] text-667085" title={r.update_date ? new Date(r.update_date).toLocaleString() : ""}>
                        {r.update_date ? `Edited ${timeAgo(r.update_date)}` : ""}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-auto pt-1">
                    {r.is_builder ? (
                      <button type="button" className="text-xs font-medium text-primary inline-flex items-center gap-1 hover:underline" disabled={busy === r.id} onClick={() => openSaved(r, false)}>
                        <Pencil size={13} /> Edit
                      </button>
                    ) : (
                      <span className="text-[10px] text-667085">Not editable here</span>
                    )}
                    <div className="ml-auto flex items-center gap-1">
                      {!r.is_default && (
                        <button type="button" className="text-xs text-344054 inline-flex items-center gap-1 border border-EAECF0 rounded-md px-2 py-1 hover:bg-f3f8ff" disabled={busy === r.id} onClick={() => makeDefault(r.id)}>
                          <CheckCircle2 size={13} /> Use
                        </button>
                      )}
                      <CardMenu
                        items={[
                          r.is_builder && { label: "Duplicate", icon: <Copy size={13} />, onClick: () => openSaved(r, true) },
                          r.is_builder && { label: "Export", icon: <Download size={13} />, onClick: () => exportOne(r) },
                          { label: "Delete", icon: <Trash2 size={13} />, onClick: () => remove(r), danger: true },
                        ]}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* create tile */}
            <button
              type="button"
              onClick={() => setShowNew(true)}
              className="rounded-xl border-2 border-dashed border-D0D5DD hover:border-primary hover:bg-white text-667085 hover:text-primary flex flex-col items-center justify-center gap-2 transition-colors"
              style={{ minHeight: THUMB_H + 96 }}
            >
              <span className="h-11 w-11 rounded-full bg-white border border-EAECF0 flex items-center justify-center">
                <Plus size={20} />
              </span>
              <span className="text-sm font-medium">New {dt.label} format</span>
              <span className="text-[11px] text-center px-6">Start from the current print, a thermal receipt or a blank page</span>
            </button>
          </div>
        </main>
      </div>
      {showNew && (
        <NewFormatModal
          docType={type}
          onClose={() => setShowNew(false)}
          onCreate={(t) => {
            setShowNew(false);
            onOpen({ template: t, id: null });
          }}
        />
      )}
    </div>
  );
}
